import type { FC, PropsWithChildren } from "react";
import { createContext, useCallback, useMemo, useState } from "react";

import type { DynamicProductData } from "@jti/magento";
import { useProductsDynamicData } from "@jti/magento";

export type ProductCardContainerContextType = {
  productsSkus: Record<string, { name?: string; categoryName?: string }>;
  products: Record<string, DynamicProductData>;
  isProductsDataLoading?: boolean;
  currency?: string;
  addToSkuList?: (sku: string, data?: { name?: string; categoryName?: string }) => void;
};

type ProductCardContainerContextProviderProps = PropsWithChildren<{
  isTrial?: boolean;
}>;

export const ProductCardContainerContext = createContext<ProductCardContainerContextType>({
  products: {},
  productsSkus: {},
});

export const ProductCardContainerContextProvider: FC<ProductCardContainerContextProviderProps> = ({
  children,
  isTrial = false,
}) => {
  const [productsSkus, setProductsSkus] = useState<Record<string, { name?: string; categoryName?: string }>>({});

  const { products, currency, isProductsDataLoaded } = useProductsDynamicData({
    productsSkus: Object.keys(productsSkus),
    isTrial,
  });

  const addToSkuList = useCallback((sku: string, data?: { name?: string; categoryName?: string }) => {
    setProductsSkus((prev) => ({ ...prev, [sku]: { ...prev[sku], ...data } }));
  }, []);

  const value = useMemo(
    () => ({ productsSkus, addToSkuList, products, currency, isProductsDataLoading: !isProductsDataLoaded }),
    [productsSkus, addToSkuList, products, currency, isProductsDataLoaded]
  );

  return <ProductCardContainerContext.Provider value={value}>{children}</ProductCardContainerContext.Provider>;
};
